import { TCustomer } from '@/types/TSmartCRM';
import { EViewType } from 'src/enums/ESmartCRM';

export interface PaginationState {
	hasNext: boolean;
	nextKey: string | null;
	pageSize: number;
	prevKeys: string[];
	hasPrev: boolean;
	nextPage: () => void;
	prevPage: () => void;
	changePageSize: (size: number) => void;
}

export interface CachedData {
	leads: TCustomer[];
	count: number;
	pagination?: any;
}

export const createInitialPaginationState = (
	pageSize = 10,
): Omit<PaginationState, 'nextPage' | 'prevPage' | 'changePageSize'> => ({
	hasNext: false,
	nextKey: null,
	pageSize,
	prevKeys: [],
	hasPrev: false,
});

export const resetPaginationState = (
	state: Omit<PaginationState, 'nextPage' | 'prevPage' | 'changePageSize'>,
): Omit<PaginationState, 'nextPage' | 'prevPage' | 'changePageSize'> => ({
	...state,
	hasNext: false,
	nextKey: null,
	prevKeys: [],
	hasPrev: false,
});

export const updatePaginationFromResponse = (
	state: Omit<PaginationState, 'nextPage' | 'prevPage' | 'changePageSize'>,
	response: any,
	pageSize: number,
): Omit<PaginationState, 'nextPage' | 'prevPage' | 'changePageSize'> => ({
	...state,
	hasNext: response?.hasMore || false,
	nextKey: response?.lastEvaluatedKey || null,
	pageSize,
	hasPrev: state.prevKeys.length > 0,
});

export class PaginationCache {
	private cache = new Map<string, CachedData>();
	private pendingRequests = new Map<string, Promise<any>>();

	getCacheKey(fieldId: string, agentId: string, view: EViewType): string {
		return `${fieldId}_${agentId}_${view}`;
	}

	has(key: string): boolean {
		return this.cache.has(key);
	}

	get(key: string): CachedData | undefined {
		return this.cache.get(key);
	}

	set(key: string, data: CachedData): void {
		this.cache.set(key, data);
	}

	clear(): void {
		this.cache.clear();
	}

	hasPendingRequest(key: string): boolean {
		return this.pendingRequests.has(key);
	}

	getPendingRequest(key: string): Promise<any> | undefined {
		return this.pendingRequests.get(key);
	}

	setPendingRequest(key: string, promise: Promise<any>): void {
		this.pendingRequests.set(key, promise);
	}

	deletePendingRequest(key: string): void {
		this.pendingRequests.delete(key);
	}
}
